import React, { useEffect, useRef, useState } from 'react';

const NewInvestmentFlow = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const mobileContainerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3, // 30%가 보이면 애니메이션 시작
        rootMargin: '0px 0px -100px 0px' // 하단에서 100px 여유
      }
    );

    // 데스크톱과 모바일 컨테이너 모두 관찰
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    if (mobileContainerRef.current) {
      observer.observe(mobileContainerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
      if (mobileContainerRef.current) {
        observer.unobserve(mobileContainerRef.current);
      }
    };
  }, []);

  return (
    <div className="mb-1 md:mb-2">
      <header className="text-left md:text-center mb-4">
        <h2 className="text-3xl font-bold text-gray-800">투자 구조</h2>
      </header>

      {/* 데스크톱 버전 */}
      <div ref={containerRef} className="hidden md:block bg-white rounded-xl p-8">
        <svg viewBox="0 0 800 600" className="w-full h-auto max-w-4xl mx-auto">
          {/* 화살표 마커 정의 - 모두 검은색 */}
          <defs>
            <marker id="arrow-black" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M 0 0 L 6 3 L 0 6 z" fill="#000000" />
            </marker>
          </defs>

          {/* 중소벤처기업부 (상단 중앙) */}
          <g transform="translate(400, 50)">
            <rect x="-100" y="0" width="200" height="120" rx="8" fill="#374151" />
            <rect x="-100" y="80" width="200" height="40" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="45" textAnchor="middle" className="text-white text-3xl">🏛️</text>
            <text x="0" y="105" textAnchor="middle" className="font-bold text-gray-800">중소벤처기업부</text>
          </g>

          {/* 투자자 (좌측 하단) */}
          <g transform="translate(150, 400)">
            <rect x="-100" y="0" width="200" height="120" rx="8" fill="#374151" />
            <rect x="-100" y="80" width="200" height="40" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="45" textAnchor="middle" className="text-white text-3xl">👤</text>
            <text x="0" y="105" textAnchor="middle" className="font-bold text-gray-800">투자자</text>
          </g>

          {/* 벤처기업 (우측 하단) */}
          <g transform="translate(650, 400)">
            <rect x="-100" y="0" width="200" height="120" rx="8" fill="#374151" />
            <rect x="-100" y="80" width="200" height="40" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="45" textAnchor="middle" className="text-white text-3xl">🏢</text>
            <text x="0" y="105" textAnchor="middle" className="font-bold text-gray-800">벤처기업</text>
          </g>

          {/* 투자자 → 벤처기업 (채권투자) */}
          <g className="arrow-group">
            <path
              d="M 260 450 L 534 450"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine 1.5s ease-out forwards' : 'none'
              }}
            />
            <path
              d="M 532 446 L 540 450 L 532 454 z"
              fill="#000000"
              className="arrow-head"
              style={{
                opacity: 0,
                transform: 'translateX(-274px)',
                animation: isVisible ? 'moveArrowHead 1.5s ease-out forwards' : 'none'
              }}
            />
          </g>
          <text
            x="400"
            y="430"
            textAnchor="middle"
            className="text-base font-bold fill-black text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 1.5s forwards' : 'none'
            }}
          >
            채권투자 (투자기간 3년)
          </text>

          {/* 벤처기업 → 투자자 (원리금 상환) */}
          <g className="arrow-group">
            <path
              d="M 540 470 L 266 470"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine2 1.5s ease-out 2s forwards' : 'none'
              }}
            />
            <path
              d="M 268 467 L 260 470 L 268 473 z"
              fill="#000000"
              className="arrow-head"
              style={{
                opacity: 0,
                transform: 'translateX(274px)',
                animation: isVisible ? 'moveArrowHead2 1.5s ease-out 2s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="400"
            y="500"
            textAnchor="middle"
            className="text-base font-bold fill-black text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            만기 시(3년) 원리금 상환
          </text>

          {/* 벤처기업 → 중소벤처기업부 (투자확인서 신청) */}
          <g className="arrow-group">
            <path
              d="M 590 390 L 460 180"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine3 1.5s ease-out 2s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="580"
            y="280"
            textAnchor="middle"
            className="text-base font-bold fill-black text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            투자확인서 신청
          </text>

          {/* 중소벤처기업부 → 투자자 (투자확인서 발급) */}
          <g className="arrow-group">
            <path
              d="M 360 180 L 210 390"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine4 1.5s ease-out 4s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="200"
            y="280"
            textAnchor="middle"
            className="text-base font-bold fill-black text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 5.5s forwards' : 'none'
            }}
          >
            <tspan x="200" dy="0">투자확인서 발급</tspan>
            <tspan x="200" dy="16">(소득공제 혜택)</tspan>
          </text>
        </svg>
      </div>

      {/* 모바일 버전 - 전체 화면 활용 */}
      <div ref={mobileContainerRef} className="md:hidden bg-white -mx-6 sm:-mx-8 px-2 py-4">
        <svg viewBox="0 0 380 550" className="w-full h-auto">
          {/* 화살표 마커 정의 - 모두 검은색 */}
          <defs>
            <marker id="arrow-black-m" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M 0 0 L 6 3 L 0 6 z" fill="#000000" />
            </marker>
          </defs>

          {/* 중소벤처기업부 (상단 중앙) */}
          <g transform="translate(190, 60)">
            <rect x="-85" y="0" width="170" height="120" rx="8" fill="#374151" />
            <rect x="-85" y="75" width="170" height="45" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="47" textAnchor="middle" fontSize="40">🏛️</text>
            <text x="0" y="102" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">중소벤처기업부</text>
          </g>

          {/* 투자자 (좌측 하단) */}
          <g transform="translate(70, 380)">
            <rect x="-65" y="0" width="130" height="120" rx="8" fill="#374151" />
            <rect x="-65" y="75" width="130" height="45" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="47" textAnchor="middle" fontSize="40">👤</text>
            <text x="0" y="102" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">투자자</text>
          </g>

          {/* 벤처기업 (우측 하단) */}
          <g transform="translate(310, 380)">
            <rect x="-65" y="0" width="130" height="120" rx="8" fill="#374151" />
            <rect x="-65" y="75" width="130" height="45" fill="white" stroke="#374151" strokeWidth="2" />
            <text x="0" y="47" textAnchor="middle" fontSize="40">🏢</text>
            <text x="0" y="102" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">벤처기업</text>
          </g>

          {/* 투자자 → 벤처기업 (채권투자) */}
          <g className="arrow-group">
            <path
              d="M 140 415 L 240 415"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine 1.5s ease-out forwards' : 'none'
              }}
            />
          </g>
          <text
            x="190"
            y="385"
            textAnchor="middle"
            fontSize="15"
            fontWeight="bold"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 1.5s forwards' : 'none'
            }}
          >
            채권투자
          </text>
          <text
            x="190"
            y="400"
            textAnchor="middle"
            fontSize="14"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 1.5s forwards' : 'none'
            }}
          >
            (투자기간 3년)
          </text>

          {/* 벤처기업 → 투자자 (원리금 상환) */}
          <g className="arrow-group">
            <path
              d="M 240 440 L 140 440"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine2 1.5s ease-out 2s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="190"
            y="468"
            textAnchor="middle"
            fontSize="15"
            fontWeight="bold"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            만기 시(3년)
          </text>
          <text
            x="190"
            y="482"
            textAnchor="middle"
            fontSize="14"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            원리금 상환
          </text>

          {/* 벤처기업 → 중소벤처기업부 (투자확인서 신청) */}
          <g className="arrow-group">
            <path
              d="M 285 375 L 235 185"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine3 1.5s ease-out 2s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="300"
            y="270"
            textAnchor="middle"
            fontSize="15"
            fontWeight="bold"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            투자확인서
          </text>
          <text
            x="300"
            y="285"
            textAnchor="middle"
            fontSize="14"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 3.5s forwards' : 'none'
            }}
          >
            신청
          </text>

          {/* 중소벤처기업부 → 투자자 (투자확인서 발급) */}
          <g className="arrow-group">
            <path
              d="M 155 185 L 95 375"
              stroke="#000000"
              strokeWidth="3"
              fill="none"
              className="arrow-line"
              pathLength="100"
              style={{
                strokeDasharray: '100',
                strokeDashoffset: '100',
                animation: isVisible ? 'drawArrowLine4 1.5s ease-out 4s forwards' : 'none'
              }}
            />
          </g>
          <text
            x="80"
            y="270"
            textAnchor="middle"
            fontSize="15"
            fontWeight="bold"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 5.5s forwards' : 'none'
            }}
          >
            투자확인서
          </text>
          <text
            x="80"
            y="285"
            textAnchor="middle"
            fontSize="14"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 5.5s forwards' : 'none'
            }}
          >
            발급
          </text>
          <text
            x="80"
            y="300"
            textAnchor="middle"
            fontSize="14"
            fill="#000000"
            className="text-animation"
            style={{
              opacity: 0,
              animation: isVisible ? 'fadeInText 0.5s ease-out 5.5s forwards' : 'none'
            }}
          >
            (소득공제)
          </text>
        </svg>
      </div>

      <style jsx>{`
        @keyframes drawArrowLine {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes drawArrowLine2 {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes drawArrowLine3 {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes drawArrowLine4 {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes moveArrowHead {
          0% {
            opacity: 0;
            transform: translateX(-274px);
          }
          1% {
            opacity: 1;
            transform: translateX(-274px);
          }
          100% {
            opacity: 1;
            transform: translateX(0px);
          }
        }

        @keyframes moveArrowHead2 {
          0% {
            opacity: 0;
            transform: translateX(274px);
          }
          1% {
            opacity: 1;
            transform: translateX(274px);
          }
          100% {
            opacity: 1;
            transform: translateX(0px);
          }
        }

        @keyframes moveArrowHead3 {
          0% {
            opacity: 0;
            transform: translate(124px, 204px);
          }
          1% {
            opacity: 1;
            transform: translate(124px, 204px);
          }
          100% {
            opacity: 1;
            transform: translate(0px, 0px);
          }
        }

        @keyframes moveArrowHead4 {
          0% {
            opacity: 0;
            transform: translate(144px, -204px);
          }
          1% {
            opacity: 1;
            transform: translate(144px, -204px);
          }
          100% {
            opacity: 1;
            transform: translate(0px, 0px);
          }
        }

        @keyframes moveArrowHeadMobile {
          0% {
            opacity: 0;
            transform: translateX(-94px);
          }
          1% {
            opacity: 1;
            transform: translateX(-94px);
          }
          100% {
            opacity: 1;
            transform: translateX(0px);
          }
        }

        @keyframes moveArrowHeadMobile2 {
          0% {
            opacity: 0;
            transform: translateX(94px);
          }
          1% {
            opacity: 1;
            transform: translateX(94px);
          }
          100% {
            opacity: 1;
            transform: translateX(0px);
          }
        }

        @keyframes fadeInText {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default NewInvestmentFlow;